import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useAuth } from '../../../hooks/use-auth';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useParams, useLocation, Link as RouterLink } from "react-router-dom";
import { useState, useEffect } from 'react';
import { userApi } from '../../../api/user-api';

export const AdminUpdateForm = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin } = location.state || {};

  const [roles, setRoles] = useState([]);
  const [avatarUrl, setAvatarUrl] = useState(admin?.avatar || '');
  const [showPassword, setShowPassword] = useState(false);

  const [initialValues, setInitialValues] = useState({
    firstname: admin?.firstname || '',
    lastname: admin?.lastname || '',
    email: admin?.email || '',
    password: '',
    avatar: admin?.avatar || '',
    role_id: admin?.role_id || '',
  });

  const validationSchema = Yup.object({
    firstname: Yup.string().required("Le prénom est requis"),
    lastname: Yup.string().required("Le nom est requis"),
    email: Yup.string().email("Email invalide").required("L'email est requis"),
    avatar: Yup.string().url("URL invalide"),
    role_id: Yup.string().required("Le rôle est requis"),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/admin/update`;
        const accessToken = localStorage.getItem("accessToken");

        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ id: admin.id, ...values }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.msg === "Cet email est déjà utilisé par un autre administrateur") {
            toast.error(errorData.msg);
          } else {
            throw new Error(`Erreur HTTP! Status: ${response.status}`);
          }
          return;
        }

        const data = await response.json();
        if (!data.id) {
          toast.success('Admin mis à jour avec succès !');
          helpers.setSubmitting(false);
        } else {
          toast.error(data.msg || "Erreur lors de la mise à jour.");
          helpers.setErrors({ submit: data.msg });
        }
      } catch (err) {
        console.error(err);
        toast.error('Une erreur est survenue !');
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    setAvatarUrl(formik.values.avatar);
  }, [formik.values.avatar]);

  useEffect(() => {
    if (admin?.role_name && roles.length > 0) {
      const role = roles.find((r) => r.role_name === admin.role_name);
      if (role) {
        setInitialValues((prev) => ({ ...prev, role_id: role.id }));
      }
    }
  }, [admin, roles]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesUrl = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/role/get/`;
        const accessToken = localStorage.getItem("accessToken");
        const res = await fetch(rolesUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) throw new Error(`Erreur HTTP! Status: ${res.status}`);
        const data = await res.json();
        setRoles(data.roles);
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la récupération des rôles.");
      }
    };
    fetchRoles();
  }, []);

  const fullname = `${admin?.firstname || ''} ${admin?.lastname || ''}`.trim() || 'Administrateur';
  const currentRoleName = roles.find(r => r.id === formik.values.role_id)?.role_name || admin?.role_name || '';

  return (
    <Box component="main" sx={{ flexGrow: 1, pt: 10, pb: 4 }}>
      <Container maxWidth="md">

        {/* Back link */}
        <RouterLink
          to={`/${globalThis.localStorage.getItem('tenant')}/dashboard/admins`}
          style={{ textDecoration: 'none' }}
        >
          <Link
            color="textPrimary"
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', mb: 3, '&:hover': { color: '#6060B8' } }}
          >
            <ArrowBackIcon fontSize="small" sx={{ mr: 0.75 }} />
            <Typography variant="subtitle2">Liste des administrateurs</Typography>
          </Link>
        </RouterLink>

        {/* Profile header card */}
        <Card sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ height: 8, background: 'linear-gradient(90deg, #6060B8 0%, #9090D0 100%)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={avatarUrl || undefined}
                sx={{ width: 64, height: 64, backgroundColor: '#9E9E9E', color: 'white', fontWeight: 700, fontSize: '1.6rem' }}
              >
                {admin?.firstname ? admin.firstname.charAt(0).toUpperCase() : '?'}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{fullname}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>{admin?.email}</Typography>
                {currentRoleName && (
                  <Chip
                    label={currentRoleName}
                    size="small"
                    icon={<AdminPanelSettingsOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{ mt: 0.75, backgroundColor: 'rgba(96,96,184,0.1)', color: '#6060B8', fontWeight: 600, height: 22 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Form card */}
        <form onSubmit={formik.handleSubmit}>
          <Card sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AdminPanelSettingsOutlinedIcon sx={{ color: '#6060B8' }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Édition du profil</Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>Modifiez les informations de l'administrateur</Typography>
              </Box>
            </Box>
            <Divider />
            <CardContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>

                {/* Prénom / Nom */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Prénom"
                    name="firstname"
                    required
                    value={formik.values.firstname}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={Boolean(formik.touched.firstname && formik.errors.firstname)}
                    helperText={formik.touched.firstname && formik.errors.firstname}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nom"
                    name="lastname"
                    required
                    value={formik.values.lastname}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={Boolean(formik.touched.lastname && formik.errors.lastname)}
                    helperText={formik.touched.lastname && formik.errors.lastname}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    required
                    value={formik.values.email}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
                  />
                </Grid>

                {/* Avatar URL + preview */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={avatarUrl || undefined}
                      sx={{ width: 52, height: 52, backgroundColor: '#9E9E9E', color: 'white', fontWeight: 700, fontSize: '1.2rem', flexShrink: 0 }}
                    >
                      {formik.values.firstname ? formik.values.firstname.charAt(0).toUpperCase() : '?'}
                    </Avatar>
                    <TextField
                      fullWidth
                      label="URL de l'avatar"
                      name="avatar"
                      value={formik.values.avatar}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={Boolean(formik.touched.avatar && formik.errors.avatar)}
                      helperText={formik.touched.avatar && formik.errors.avatar || "Lien vers une image de profil (optionnel)"}
                      InputProps={{ startAdornment: <InputAdornment position="start"><LinkOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
                    />
                  </Box>
                </Grid>

                {/* Rôle */}
                <Grid item xs={12}>
                  <FormControl fullWidth error={Boolean(formik.touched.role_id && formik.errors.role_id)}>
                    <InputLabel>Rôle</InputLabel>
                    <Select
                      name="role_id"
                      label="Rôle"
                      value={formik.values.role_id}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>{role.role_name}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.role_id && formik.errors.role_id && (
                      <FormHelperText>{formik.errors.role_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

              </Grid>
            </CardContent>

            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, py: 2, gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/${globalThis.localStorage.getItem('tenant')}/dashboard/admins`)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={formik.isSubmitting}
                startIcon={<SaveOutlinedIcon />}
                sx={{ backgroundColor: '#6060B8', '&:hover': { backgroundColor: '#4a4a9a' } }}
              >
                Enregistrer
              </Button>
            </Box>
          </Card>
        </form>
      </Container>
    </Box>
  );
};
